import { createContext, useContext, useEffect, useMemo, useState } from "react";
import API from "../api/api";

const AuthContext = createContext(null);

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

// Where the account may act: "platform" (manages vendors and vendor users only), "vendor" (works inside its one vendor) or "invalid" (role and
// vendor do not fit together: nothing is offered). The server says so; it is derived from role and vendor only when an older reply lacks it.
function scopeOf(user) {
  if (user.scope) return user.scope;
  const vendorId = String(user.vendorId || "").trim();
  if (user.role === "superadmin") return vendorId === "all" ? "platform" : "invalid";
  return vendorId && vendorId.toLowerCase() !== "all" ? "vendor" : "invalid";
}

function normalizeUser(user) {
  if (!user) return null;
  const role = normalizeRole(user.role);
  return {
    ...user,
    role,
    scope: scopeOf({ ...user, role }),
  };
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("yansh_admin_token");
    if (!token) {
      setBooting(false);
      return;
    }

    API.get("/admin/session")
      .then((res) => setUser(normalizeUser(res.data.user)))
      .catch(() => {
        localStorage.removeItem("yansh_admin_token");
        setUser(null);
      })
      .finally(() => setBooting(false));
  }, []);

  useEffect(() => {
    const handleExpiredSession = () => {
      setUser(null);
      setBooting(false);
    };

    window.addEventListener("yansh:admin-session-expired", handleExpiredSession);
    return () => window.removeEventListener("yansh:admin-session-expired", handleExpiredSession);
  }, []);

  const login = async (credentials) => {
    const res = await API.post("/admin/login", credentials);
    localStorage.setItem("yansh_admin_token", res.data.token);
    const normalizedUser = normalizeUser(res.data.user);
    setUser(normalizedUser);
    return normalizedUser;
  };

  // After the password is changed the server issues a fresh token (the old ones ended); keep it and read the session again.
  const acceptToken = async (token) => {
    if (token) localStorage.setItem("yansh_admin_token", token);
    const res = await API.get("/admin/session", { suppressGlobalError: true });
    setUser(normalizeUser(res.data.user));
  };

  const logout = () => {
    localStorage.removeItem("yansh_admin_token");
    setUser(null);
  };

  const value = useMemo(() => ({
    user,
    booting,
    isAuthenticated: Boolean(user),
    scope: user?.scope || "",
    isPlatform: user?.scope === "platform",
    login,
    acceptToken,
    logout,
    hasRole: (roles) => {
      const currentRole = normalizeRole(user?.role);
      return Boolean(currentRole && (roles || []).map(normalizeRole).includes(currentRole));
    },
  }), [booting, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}

export { AuthProvider };
export default AuthProvider;
